import { createStyles, MantineProvider } from "@mantine/core";
import { rpaTheme } from "../../theme/rpaTheme";
import { OrdersTableFooter, OrdersTableV2 } from "./OrdersTableV2";
import { GraphQL } from "@rpi/openapi-api";
import { PaginationV2 } from "../../components/PaginationV2";
import React from "react";
import { useRpiPagination } from "../../hooks/usePagination";
import { useAuthUser } from "../../hooks/useAuthUser";
import { tokens, Input, Button } from "@blurb/codex-react";
import { useForm } from "@mantine/form";
import { IconV3 } from "@rpi/openapi-core";
import { AddPaymentMethod } from "../../components/Alerts/AddPaymentMethodAlert";

const useStyles = createStyles({
  container: {
    width: "100%",
    height: "100%",
    overflow: "auto",
    backgroundColor: tokens.color.primitive.light.calm[50],
    padding: tokens.spacing[20],
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[6],
  },
  header: {
    fontFamily: tokens.font.family.heading,
    "font-weight": tokens.font.weight.semibold,
    fontSize: tokens.font.size["6xl"],
    lineHeight: tokens.font.lineHeight.tight,
    color: tokens.color.semantic.text.bold,
  },
  filtersContainer: { display: "flex", flexDirection: "row", alignItems: "center", height: 40 },
  filtersComponents: { display: "flex", flexDirection: "row", alignItems: "center", gap: tokens.spacing[2] },
  searchInput: {
    backgroundColor: tokens.color.semantic.bg.input,
    width: 400,
  },
  searchButton: { backgroundColor: tokens.color.semantic.bg.input },
  table: {
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 4px 12px 1px rgba(0, 0, 0, 0.10)",
    borderRadius: tokens.radius.lg,
  },
});
export const OrdersViewV2 = () => {
  const user = useAuthUser();
  const pagination = useRpiPagination({ perPage: 20 });
  const { classes } = useStyles();

  // Table Filters
  const [sortInput, setSortInput] = React.useState<GraphQL.SortInput | undefined>({
    by: GraphQL.OrderSearchField.CreatedAt,
    sortDirection: GraphQL.SortDirection.Desc,
  });

  const [generalSearch, setGeneralSearch] = React.useState<string | undefined>();

  const sandboxFilter = {
    field: GraphQL.OrderSearchField.IsSandbox,
    condition: GraphQL.SearchCondition.Equals,
    firstValue: user.isSandbox.toString(),
  };

  const form = useForm<{ searchValue: string }>({ initialValues: { searchValue: "" } });

  const handleSearchOrdersSubmit = (input: { searchValue: string }) => {
    setGeneralSearch(input.searchValue || undefined);
    pagination.firstPage();
  };

  const { data, isLoading, error } = GraphQL.useOrdersQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: [sandboxFilter],
        generalInput: generalSearch, // generalInput searches by customerOrderId
        sort: sortInput,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.orders.totalCount),
    }
  );
  const orders = data?.orders.data;

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={classes.container} data-testid="orders-view" aria-label="Orders view">
        <span className={classes.header}>Orders</span>
        <AddPaymentMethod />
        <div className={classes.filtersContainer}>
          <form className={classes.filtersComponents} onSubmit={form.onSubmit(handleSearchOrdersSubmit)}>
            <div className={classes.searchInput}>
              <Input
                iconLeft={<IconV3 icon="search" size={24} />}
                placeholder="Search by Order ID"
                {...form.getInputProps("searchValue")}
              />
            </div>
            <Button variant="outlined" intent="neutral" type="submit" className={classes.searchButton}>
              Search
            </Button>
          </form>
        </div>
        <div className={classes.table} data-testid="orders-table" aria-label="Orders table">
          <OrdersTableV2
            isLoading={isLoading}
            error={error}
            orders={orders}
            withSortBy={{
              defaultValue: sortInput,
              onChange: setSortInput,
            }}
          >
            <OrdersTableFooter>
              <PaginationV2 pagination={pagination} />
            </OrdersTableFooter>
          </OrdersTableV2>
        </div>
      </div>
    </MantineProvider>
  );
};
